import type { JwtTokenPayload } from '@/features/auth/models/jwt-token-payload';
import type { User } from '@/features/participant';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ConversationCreate } from '../models/conversation-dto';

const resolver = classValidatorResolver(ConversationCreate);

export const useConversationForm = (currentUser?: Nullable<JwtTokenPayload>) => {
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const formMethods = useForm<ConversationCreate>({
    resolver,
    defaultValues: new ConversationCreate(),
    reValidateMode: 'onChange'
  });

  const { handleSubmit, control, reset, watch, setValue } = formMethods;

  // Watch form changes to track unsaved changes
  const watchedValues = watch();
  useEffect(() => {
    const hasChanges = watchedValues.conversationName !== '' || selectedUsers.length > 0;
    setHasUnsavedChanges(hasChanges);
  }, [watchedValues.conversationName, selectedUsers.length]);

  // Sync selected users with form data for validation
  useEffect(() => {
    const participantIds = selectedUsers.map((u) => u.id);
    if (currentUser?.user_id) {
      participantIds.push(currentUser.user_id);
    }
    setValue('participantIds', participantIds);
  }, [selectedUsers, currentUser?.user_id, setValue]);

  const handleRemoveUser = useCallback((userToRemove: User) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userToRemove.id));
  }, []);

  const handleReset = useCallback(() => {
    reset();
    setSelectedUsers([]);
    setHasUnsavedChanges(false);
  }, [reset]);

  return {
    // Form
    formMethods,
    handleSubmit,
    control,
    reset,

    // State
    selectedUsers,
    setSelectedUsers,
    hasUnsavedChanges,

    // Actions
    handleRemoveUser,
    handleReset
  };
};
